// Human-readable explanations for SignatureDeliveryLog.outcome values
// (backend/signatures/models.py OUTCOME_CHOICES). Keep in sync by hand.
// `detail` from the backend is a raw machine string (e.g. "groups: A, B"),
// so it's parsed here instead of being shown as-is.
import { getLang } from '../i18n';

const NO_ACTION_HINT = {
  tr: 'Kullanıcının Outlook imzası olduğu gibi kalır, eklenti hiçbir şey eklemez.',
  en: 'The user’s Outlook signature stays as it is; the add-in adds nothing.',
};

const OUTCOMES = {
  override: { label: { tr: 'Kişiye özel imza', en: 'Personal signature' } },
  user_selected: { label: { tr: 'Kullanıcının seçtiği şablon', en: 'User-selected template' } },
  group_template: { label: { tr: 'Grup şablonu', en: 'Group template' } },
  no_template_match: {
    label: { tr: 'Şablon atanmamış', en: 'No template assigned' },
    reason: {
      tr: () => 'Kullanıcıya atanmış bir şablon yok.',
      en: () => 'No template is assigned to this user.',
    },
  },
  no_groups: {
    label: { tr: 'Grup atanmamış', en: 'No groups' },
    reason: {
      tr: () => `Kullanıcı hiçbir gruba üye değil, bu yüzden şablon seçilemedi. ${NO_ACTION_HINT.tr}`,
      en: () => `The user isn’t a member of any group, so no template could be chosen. ${NO_ACTION_HINT.en}`,
    },
  },
  user_not_found: {
    label: { tr: 'Dizinde bulunamadı', en: 'Not found in directory' },
    reason: {
      tr: () => `Kullanıcı bağlı dizinde (Microsoft 365 / Google Workspace) bulunamadı. ${NO_ACTION_HINT.tr}`,
      en: () => `The user wasn’t found in the connected directory (Microsoft 365 / Google Workspace). ${NO_ACTION_HINT.en}`,
    },
  },
  org_not_found: {
    label: { tr: 'Organizasyon bulunamadı', en: 'Organization not found' },
    reason: {
      tr: (detail) => `Bu e-posta alan adına${detail ? ` (${detail})` : ''} kayıtlı bir organizasyon yok.`,
      en: (detail) => `No organization is registered for this email domain${detail ? ` (${detail})` : ''}.`,
    },
  },
  org_ambiguous: {
    label: { tr: 'Organizasyon belirsiz', en: 'Ambiguous organization' },
    reason: {
      tr: (detail) => `Aynı alan adı${detail ? ` (${detail})` : ''} birden fazla organizasyonda kayıtlı, hangisinin kullanılacağı belirlenemedi.`,
      en: (detail) => `The same domain${detail ? ` (${detail})` : ''} is registered to more than one organization, so it couldn’t be determined which to use.`,
    },
  },
  missing_email: {
    label: { tr: 'E-posta eksik', en: 'Missing email' },
    reason: { tr: () => 'İstekte e-posta adresi gönderilmemiş.', en: () => 'The request didn’t include an email address.' },
  },
  invalid_email: {
    label: { tr: 'Geçersiz e-posta', en: 'Invalid email' },
    reason: { tr: () => 'İstekteki e-posta adresi geçerli bir formatta değil.', en: () => 'The email address in the request isn’t in a valid format.' },
  },
  rate_limited: {
    label: { tr: 'Hız sınırı aşıldı', en: 'Rate limited' },
    reason: { tr: () => 'Kısa sürede çok fazla istek geldiği için istek geçici olarak reddedildi.', en: () => 'The request was temporarily rejected because too many requests arrived in a short time.' },
  },
  server_error: {
    label: { tr: 'Sunucu hatası', en: 'Server error' },
    reason: {
      tr: (detail) => `Sunucuda beklenmeyen bir hata oluştu${detail ? `: ${detail}` : '.'}`,
      en: (detail) => `An unexpected server error occurred${detail ? `: ${detail}` : '.'}`,
    },
  },
};

// 'success' | 'unassigned' | 'failure'. "No template assigned" isn't a
// failure — the add-in correctly left the mail alone (backend:
// SignatureDeliveryLog.UNASSIGNED_OUTCOMES / .status).
const UNASSIGNED_OUTCOMES = ['no_template_match', 'no_groups'];

export function deliveryStatus(entry) {
  if (entry.status) return entry.status;
  if (entry.is_success) return 'success';
  return UNASSIGNED_OUTCOMES.includes(entry.outcome) ? 'unassigned' : 'failure';
}

// { label, reason } — reason is '' for successful outcomes and unknown
// outcomes (falls back to the raw outcome string as the label). Uses the
// current UI language; callers re-render on a language switch.
export function describeDeliveryOutcome(entry, lang = getLang()) {
  const known = OUTCOMES[entry.outcome];
  if (!known) return { label: entry.outcome, reason: entry.detail || '' };
  const l = lang === 'en' ? 'en' : 'tr';
  return { label: known.label[l], reason: known.reason ? known.reason[l](entry.detail || '') : '' };
}
